import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    input,
    InputSignal,
    output,
    OutputEmitterRef,
    Signal,
    viewChild,
} from '@angular/core';

import { SelectedFileComponent } from '@connect-services/client/components/drag-and-drop/selected-file/selected-file.component';
import { RessourcesModule } from '@ressources/client/ressources.module';

export enum DragDropStatusEnum {
    ERROR = 'error',
    SIZE_ERROR = 'size_error',
    SUCCESS = 'success',
}

export enum DragAndDropTypeEnum {
    DEPOSIT = 'deposit',
    DEFAULT = 'default',
}

const MAX_DEPOSITS_ON_FOLDER = 10;

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RessourcesModule, SelectedFileComponent],
    selector: 'app-drag-and-drop',
    templateUrl: './drag-and-drop.component.html',
})
export class DragAndDropComponent {
    public readonly acceptedFileExtensions: InputSignal<string[]> = input.required<string[]>();
    public readonly depositCount = input<number>(0);
    public readonly dragAndDropType: InputSignal<DragAndDropTypeEnum> = input<DragAndDropTypeEnum>(
        DragAndDropTypeEnum.DEFAULT,
    );
    public readonly maxBytesSize: InputSignal<number> = input<number>(Number.POSITIVE_INFINITY);
    public readonly maxFileAmount: InputSignal<number> = input<number>(1);
    public newItemEvent: OutputEmitterRef<File[]> = output<File[]>();

    protected readonly acceptedFileTypes = computed<string>(() => {
        return this.normalizedAcceptedFileExtensions().join(',');
    });
    protected readonly DragAndDropTypeEnum = DragAndDropTypeEnum;
    protected readonly DragDropStatusEnum = DragDropStatusEnum;
    protected readonly fileInput: Signal<ElementRef<HTMLInputElement>> =
        viewChild.required<ElementRef<HTMLInputElement>>('fileInput');
    protected MAX_DEPOSITS_ON_FOLDER = MAX_DEPOSITS_ON_FOLDER;
    protected readonly normalizedAcceptedFileExtensions = computed<string[]>(() => {
        return this.acceptedFileExtensions().map((extension: string): string => {
            const normalizedExtension = extension.trim().toLowerCase();

            return normalizedExtension.startsWith('.') ? normalizedExtension : `.${normalizedExtension}`;
        });
    });
    protected selectedFiles: File[] = [];
    protected status: DragDropStatusEnum = DragDropStatusEnum.SUCCESS;

    public deleteFileAtIndex(index: number): void {
        this.selectedFiles.splice(index, 1);

        this.newItemEvent.emit(this.selectedFiles);

        if (this.status !== DragDropStatusEnum.SIZE_ERROR) {
            return;
        }

        const importSize: number = this.selectedFiles.reduce((acc: number, file: File): number => acc + file.size, 0);

        if (importSize > this.maxBytesSize()) {
            return;
        }

        this.status = DragDropStatusEnum.SUCCESS;
    }

    protected filterAcceptedFiles(fileList: FileList): File[] {
        const files = Array.from(fileList);
        const acceptedExtensions = this.normalizedAcceptedFileExtensions();

        const hasTooManyFiles = files.length > this.maxFileAmount();

        const hasUnsupportedFiles = files.some((file: File): boolean => {
            const fileName = file.name.toLowerCase();

            return !acceptedExtensions.some((extension: string): boolean => fileName.endsWith(extension));
        });

        if (hasTooManyFiles || hasUnsupportedFiles) {
            this.status = DragDropStatusEnum.ERROR;

            return [];
        }

        this.status = DragDropStatusEnum.SUCCESS;

        return files;
    }

    protected onDrop(event: DragEvent): void {
        if (!event.dataTransfer) {
            return;
        }

        this.processFilesSelection(event.dataTransfer.files);
    }

    protected onFilesInputUpload(event: Event): void {
        if (!(event.target instanceof HTMLInputElement) || !event.target.files) {
            return;
        }

        this.processFilesSelection(event.target.files);
    }

    protected triggerInputClick(): void {
        this.fileInput().nativeElement.click();
    }

    private processFilesSelection(files: FileList): void {
        this.selectedFiles = this.filterAcceptedFiles(files);
        this.newItemEvent.emit(this.selectedFiles);

        if (!this.selectedFiles.length) {
            return;
        }

        const importSize = this.selectedFiles.reduce(
            (totalSize: number, file: File): number => totalSize + file.size,
            0,
        );

        if (importSize <= this.maxBytesSize()) {
            return;
        }

        this.status = DragDropStatusEnum.SIZE_ERROR;
        this.newItemEvent.emit([]);
    }
}
